"use client";
import React, { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { Rocket, CreditCard, Receipt, Cloud, Apple, Repeat2 } from "lucide-react";
import { Footer } from '../components/landing/Footer';
import { useTranslation } from '../hooks/useTranslation';
import { HeroContent } from '../components/landing/HeroContent';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { featuresTranslations } from '../i18n/translations/pages';
import { LazyVideo } from '../components/shared/LazyVideo';

// Shared type for feature items
type FeatureItem = {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
};

const features = [
  {
    icon: <Rocket className="h-8 w-8 text-neutral-200" />,
    content: (
      <div className="relative">
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 rounded-xl shadow-xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none z-10"></div>
          
          <LazyVideo
            src="/videos/add_company_name_nopadding.webm"
            className="w-full aspect-video rounded-xl"
            autoPlay={true}
            loop={true}
            muted={true}
            controls={false}
            playsInline={true}
          />
        </div>
      </div>
    ),
  },
  {
    icon: <CreditCard className="h-8 w-8 text-neutral-200" />,
    content: (
      <div className="relative p-8 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
        
        <div className="grid grid-cols-3 gap-8 relative z-10">
          {[
            { src: "/images/payment-logos/visa.svg", alt: "Visa" },
            { src: "/images/payment-logos/mastercard.svg", alt: "Mastercard" },
            { src: "/images/payment-logos/american-express.svg", alt: "American Express" },
            { src: "/images/payment-logos/apple-pay.svg", alt: "Apple Pay" },
            { src: "/images/payment-logos/google-pay.svg", alt: "Google Pay" },
            { src: "/images/payment-logos/paypal.svg", alt: "PayPal" },
            { src: "/images/payment-logos/sepa.svg", alt: "SEPA" },
            { src: "/images/payment-logos/ideal.svg", alt: "iDeal" },
            { src: "/images/payment-logos/klarna.svg", alt: "Klarna" }
          ].map((logo) => (
            <div 
              key={logo.alt}
              className="flex items-center justify-center p-4 transition-all duration-300"
            >
              <img 
                src={logo.src} 
                alt={logo.alt} 
                className="h-14 w-auto object-contain filter drop-shadow-md transition-all duration-300 hover:opacity-90 hover:scale-105" 
              />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    icon: <Receipt className="h-8 w-8 text-neutral-200" />,
    content: (
      <div className="relative">
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 p-8 rounded-xl shadow-xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
          
          {/* Symbolic representation of recurring invoices */}
          <div className="flex items-center justify-center mb-12 relative">
            <div className="relative">
              {/* Main invoice */}
              <div className="w-32 h-40 bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg flex flex-col p-3 z-30 relative">
                <div className="h-2 w-16 bg-white/20 rounded-md mb-2"></div>
                <div className="h-2 w-12 bg-white/20 rounded-md mb-4"></div>
                <div className="h-2 w-20 bg-white/20 rounded-md mb-2"></div>
                <div className="h-2 w-14 bg-white/20 rounded-md mb-2"></div>
                <div className="h-2 w-18 bg-white/20 rounded-md"></div>
                <div className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-gray-800/70 flex items-center justify-center">
                  <Repeat2 className="w-8 h-8 text-white/80" />
                </div>
              </div>
              
              {/* Recurring copies (stacked behind) */}
              <div className="w-32 h-40 bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg absolute top-2 -left-2 z-20 rotate-[-4deg]"></div>
              <div className="w-32 h-40 bg-white/10 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg absolute top-4 -left-4 z-10 rotate-[-8deg]"></div>
            </div>
          </div>
          
          {/* Recurring timeline */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="h-1 w-12 bg-white/20 rounded-full"></div>
            <div className="flex flex-col items-center">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/60">
                  <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
              </div>
            </div>
            <div className="h-1 w-12 bg-white/20 rounded-full"></div>
            <div className="flex flex-col items-center">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/60">
                  <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
              </div>
            </div>
            <div className="h-1 w-12 bg-white/20 rounded-full"></div>
            <div className="flex flex-col items-center">
              <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/60">
                  <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                </svg>
              </div>
            </div>
            <div className="h-1 w-12 bg-white/20 rounded-full"></div>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <Cloud className="h-8 w-8 text-neutral-200" />,
    content: (
      <div className="relative">
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 p-8 rounded-xl shadow-xl">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.1)]">
              <Cloud className="h-8 w-8 text-primary-600" />
            </div>
          </div>
          
          <div className="flex justify-center gap-4 mb-6">
            {/* Google */}
            <div className="relative group">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)] transition-transform duration-300 group-hover:scale-105">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </div>
            </div>
            
            {/* Microsoft */}
            <div className="relative group">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)] transition-transform duration-300 group-hover:scale-105">
                <svg viewBox="0 0 23 23" className="w-6 h-6">
                  <path fill="#f25022" d="M1 1h10v10H1z" />
                  <path fill="#00a4ef" d="M1 12h10v10H1z" />
                  <path fill="#7fba00" d="M12 1h10v10H12z" />
                  <path fill="#ffb900" d="M12 12h10v10H12z" />
                </svg>
              </div>
            </div>
            
            {/* Apple */}
            <div className="relative group">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)] transition-transform duration-300 group-hover:scale-105">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="#000" d="M14.94 5.19A4.38 4.38 0 0 0 16 2a4.44 4.44 0 0 0-3 1.52 4.17 4.17 0 0 0-1 3.09 3.69 3.69 0 0 0 2.94-1.42zm2.52 7.44a4.51 4.51 0 0 1 2.16-3.81 4.66 4.66 0 0 0-3.66-2c-1.56-.16-3 .91-3.83.91s-2-.89-3.3-.87a4.92 4.92 0 0 0-4.14 2.53C2.93 12.45 4.24 17 6 19.47c.8 1.21 1.8 2.58 3.12 2.53s1.75-.82 3.28-.82 2 .82 3.3.79s2.22-1.23 3.06-2.45a11 11 0 0 0 1.38-2.85 4.41 4.41 0 0 1-2.68-4.04z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="text-center text-white/70 text-sm">
            Secure, fast, and convenient
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <Apple className="h-8 w-8 text-neutral-200" />,
    content: (
      <div className="relative">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-lg shadow-lg border border-gray-700">
          <div className="flex space-x-2 mb-4">
            <div className="h-3 w-3 rounded-full bg-red-500"></div>
            <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
            <div className="h-3 w-3 rounded-full bg-green-500"></div>
          </div>
          <div className="h-4 w-3/4 bg-white/10 rounded-md mb-3"></div>
          <div className="h-4 w-1/2 bg-white/10 rounded-md mb-3"></div>
          <div className="h-4 w-2/3 bg-white/10 rounded-md"></div>
        </div>
      </div>
    ),
  }
];

export function FeaturesWithStickyScroll({ content = features }: { content?: FeatureItem[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const backgrounds = ["#18181b", "#27272a", "#18181b", "#27272a", "#18181b", "#27272a"];

  const [gradient, setGradient] = React.useState(backgrounds[0]);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const cardsBreakpoints = content.map(
      (_, index) => index / content.length
    );
    const closestBreakpointIndex = cardsBreakpoints.reduce(
      (acc, breakpoint, index) => {
        const distance = Math.abs(latest - breakpoint);
        if (distance < Math.abs(latest - cardsBreakpoints[acc])) {
          return index;
        }
        return acc;
      },
      0
    );
    setGradient(backgrounds[closestBreakpointIndex % backgrounds.length]);
  });
  return (
    <motion.div
      animate={{
        background: gradient,
      }}
      transition={{
        duration: 0.5,
      }}
      ref={ref}
      className="w-full relative h-full"
    >
      <StickyScroll content={content} />
    </motion.div>
  );
}

export const StickyScroll = ({
  content,
}: {
  content: FeatureItem[];
}) => {
  return (
    <div className="py-4 md:pb-20">
      <motion.div className="hidden lg:flex h-full flex-col max-w-7xl mx-auto justify-between relative p-10">
        {content.map((item, index) => (
          <ScrollContent key={`feature-${index}`} item={item} index={index} />
        ))}
      </motion.div>
      <motion.div className="flex lg:hidden flex-col max-w-7xl mx-auto justify-between relative p-10">
        {content.map((item, index) => (
          <ScrollContentMobile
            key={`feature-mobile-${index}`}
            item={item}
            index={index}
          />
        ))}
      </motion.div>
    </div>
  );
};

export const ScrollContent = ({
  item,
  index,
}: {
  item: FeatureItem;
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const translate = useTransform(scrollYProgress, [0, 1], [0, 250]);
  const translateContent = useTransform(scrollYProgress, [0, 1], [150, 0]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.05, 0.5, 0.7, 1],
    [0, 1, 1, 0, 0]
  );

  const opacityContent = useTransform(
    scrollYProgress,
    [0, 0.2, 0.5, 0.8, 1],
    [0, 0, 1, 1, 0]
  );

  return (
    <motion.div
      ref={ref}
      transition={{
        duration: 0.3,
      }}
      key={`scroll-content-${index}`}
      className="my-40 relative grid grid-cols-2 gap-8"
    >
      <div className="w-full">
        <motion.div
          style={{
            y: translate,
            opacity: index === 0 ? opacityContent : 1,
          }}
          className=""
        >
          <div>{item.icon}</div>
          <motion.h2 className="max-w-md mt-2 font-bold text-2xl lg:text-4xl inline-block bg-clip-text text-left text-transparent bg-gradient-to-b from-white to-white/80">
            {item.title || ''}
          </motion.h2>

          <motion.p className="text-lg text-neutral-400 font-regular max-w-sm mt-2">
            {item.description || ''}
          </motion.p>
        </motion.div>
      </div>
      <motion.div
        key={`content-${index}`}
        style={{
          y: translateContent,
          opacity: opacity,
        }}
        className="h-full w-full rounded-md self-start"
      >
        {item.content && item.content}
      </motion.div>
    </motion.div>
  );
};

export const ScrollContentMobile = ({
  item,
  index,
}: {
  item: FeatureItem;
  index: number;
}) => {
  return (
    <motion.div
      transition={{
        duration: 0.3,
      }}
      key={`mobile-content-${index}`}
      className="my-10 relative flex flex-col md:flex-row md:gap-20"
    >
      <motion.div
        key={`mobile-visual-${index}`}
        className="w-full rounded-md self-start mb-8"
      >
        {item.content && item.content}
      </motion.div>
      <div className="w-full">
        <motion.div className="mb-6">
          <div>{item.icon}</div>
          <motion.h2 className="mt-2 font-bold text-2xl lg:text-4xl inline-block bg-clip-text text-left text-transparent bg-gradient-to-b from-white to-white/80">
            {item.title || ''}
          </motion.h2>

          <motion.p className="text-sm md:text-base text-neutral-400 font-bold max-w-sm mt-2">
            {item.description || ''}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

const FeaturesPage: React.FC = () => {
  const { locale } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  
  // Get translations for current language, fallback to English if not available
  const translations = featuresTranslations[locale as keyof typeof featuresTranslations] || featuresTranslations.en;
  
  // Update features with translations
  const translatedFeatures = features.map((feature, index) => ({
    ...feature,
    title: translations.features[index]?.title || '',
    description: translations.features[index]?.description || ''
  }));
  
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* Features Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              {translations.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              {translations.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Features Content */}
      <div className="w-full">
        <FeaturesWithStickyScroll content={translatedFeatures} />
      </div>
      <Footer />
    </div>
  );
};

export default FeaturesPage; 